import { css } from '@emotion/react';
import { SpecialOffer } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import { useTranslation, Trans } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  FieldProps,
  MultiselectField,
  Text,
  TextareaField,
  Button,
  DateRangeField,
  DropdownField,
  useControlledField,
} from '@frontend/design-system';
import { MAX_LENGTH_TEXTAREA } from '../../../constants';
import { formatDateForApi, formatDateForDisplay } from '../../../utils/helpers';
import { AiMessagingBadge, BadgeNodeTitle } from '../../ai-message-with-icon';
import { FormWrapper } from '../../form-wrapper';
import { PatientBillingConfigType } from './config';

export const PaymentTypes = ({
  options,
  ...props
}: FieldProps<PatientBillingConfigType, 'paymentTypes'> & { options: string[] }) => {
  const { t } = useTranslation('ai-receptionist-settings');
  return (
    <FormWrapper.Field>
      <Text weight='bold'>{t('Payment Types')}</Text>
      <Text>{t('What forms of payment do you accept?')}</Text>
      <MultiselectField label={t('Payment Types')} {...props}>
        {PAYMENT_OPTIONS.map((label, index) => {
          return (
            <MultiselectField.Option key={index} value={label}>
              {label}
            </MultiselectField.Option>
          );
        })}
      </MultiselectField>
    </FormWrapper.Field>
  );
};

const PAYMENT_OPTIONS = ['Cash', 'Check', 'Credit Card', 'Debit Card'];

export const BillingRelated = (props: FieldProps<PatientBillingConfigType, 'billingRelated'>) => {
  const { t } = useTranslation('ai-receptionist-settings');
  return (
    <FormWrapper.Field>
      <Text weight='bold'>{t('Billing Questions')}</Text>
      <Text>
        {t(
          'How would you like your AI Receptionist to respond if a patient has a question about their bill? (Please include the email address where billing inquiries should be directed, if applicable.)'
        )}
      </Text>
      <TextareaField maxLength={MAX_LENGTH_TEXTAREA} label={t('Billing Contact')} {...props} />
    </FormWrapper.Field>
  );
};

export const NewPatientConsultationBilling = (
  props: FieldProps<PatientBillingConfigType, 'newPatientConsultationBilling'>
) => {
  const { t } = useTranslation('ai-receptionist-settings');
  return (
    <FormWrapper.Field>
      <Text>
        {t(
          "How would you like your AI Receptionist to respond when asked about the cost of the initial consultation for a new patient? (Please include the fee or mention if it's free, along with any additional details — such as whether it is covered by insurance.)"
        )}
      </Text>
      <TextareaField maxLength={MAX_LENGTH_TEXTAREA} label={t('Initial Consultation Cost')} {...props} />
    </FormWrapper.Field>
  );
};

export const CostOfCleaning = (props: FieldProps<PatientBillingConfigType, 'costOfCleaning'>) => {
  const { t } = useTranslation('ai-receptionist-settings');
  return (
    <FormWrapper.Field>
      <Text>
        {t(
          'How would you like your AI Receptionist to respond when someone asks about the cost of a cleaning? (Please provide the price range or any relevant details, such as whether the cost varies based on the type of cleaning.)'
        )}
      </Text>
      <TextareaField maxLength={MAX_LENGTH_TEXTAREA} label={t('Cost of Cleaning')} {...props} />
    </FormWrapper.Field>
  );
};

export const SpecialOffers = ({
  children,
  setSpecialOffers,
}: {
  children: React.ReactNode;
  setSpecialOffers: React.Dispatch<React.SetStateAction<SpecialOffer[]>>;
}) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const addNewOffer = () => {
    const newOffer: SpecialOffer = {
      patientType: '',
      description: '',
      startDate: '',
      endDate: '',
    };
    setSpecialOffers((prevOffers) => [...prevOffers, { ...newOffer }]);
  };

  return (
    <FormWrapper.Field>
      <AiMessagingBadge title={<BadgeNodeTitle title={t('Special Offers')} />} />
      {children}
      <Button variant='tertiary' onClick={addNewOffer} iconName='plus' css={{ width: 'fit-content' }}>
        {t('Add New Offer')}
      </Button>
    </FormWrapper.Field>
  );
};

export const SpecialOfferFields = ({
  offer,
  setSpecialOffers,
  index,
}: {
  offer: SpecialOffer;
  setSpecialOffers: React.Dispatch<React.SetStateAction<SpecialOffer[]>>;
  index: number;
}) => {
  const { t } = useTranslation('ai-receptionist-settings');

  const updateOffer = (updates: Partial<SpecialOffer>) => {
    setSpecialOffers((prevOffers) => prevOffers.map((o, i) => (i === index ? { ...o, ...updates } : o)));
  };

  const removeOffer = () => {
    setSpecialOffers((prevOffers) =>
      prevOffers.filter((_, i) => i !== index).map((o, i) => ({ ...o, offerNumber: i + 1 }))
    );
  };

  const datesRangeProps = useControlledField({
    type: 'dateRange',
    value: [formatDateForDisplay(offer.startDate), formatDateForDisplay(offer.endDate)],
    onChange: (value: string[]) =>
      updateOffer({ startDate: formatDateForApi(value[0]), endDate: formatDateForApi(value[1]) }),
    required: true,
    validateOnChange: true,
  });

  const dropdownProps = useControlledField({
    type: 'dropdown',
    value: offer.patientType,
    onChange: (value: string) => updateOffer({ patientType: value }),
    required: true,
  });

  const textAreaProps = useControlledField({
    type: 'text',
    value: offer.description,
    onChange: (value: string) => updateOffer({ description: value }),
    required: true,
  });

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <Text weight='bold' size='medium'>
          <Trans t={t} offerNumber={index}>
            Offer {{ offerNumber: index + 1 }}
          </Trans>
        </Text>
        <Button iconName='x' destructive variant='tertiary' onClick={removeOffer}>
          {t('Remove')}
        </Button>
      </div>
      <DropdownField label={t('Select Segment')} name='select-segement' {...dropdownProps}>
        <DropdownField.Option value='NEW_PATIENT'>{t('New Patient')}</DropdownField.Option>
        <DropdownField.Option value='EXISTING_PATIENT'>{t('Existing Patient')}</DropdownField.Option>
        <DropdownField.Option value='ALL_PATIENTS'>{t('All Patients')}</DropdownField.Option>
        <DropdownField.Option value='CHILDREN'>{t('Children')}</DropdownField.Option>
        <DropdownField.Option value='CUSTOM'>{t('Custom')}</DropdownField.Option>
      </DropdownField>
      <DateRangeField {...datesRangeProps} label='' name='dates' />
      <TextareaField {...textAreaProps} label={t('Offer Description')} name='offer-description' />
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.medium};
    padding: ${theme.spacing(2)};
  `,
  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(4)};
  `,
};
